<script lang="ts">import { AsyncRaceCurrent, AsyncRaceLeaderboard } from "../api/api";
import { formatBaseCurrency } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { Api, isMobile, loggedIn, ModalDialog, selectedProfile, stateOpenModal, statePlayerId, truncateText } from "@infinitro/global";
import { RemoteIcon } from "@infinitro/icons";
import { Icon } from "@infinitro/rank-badges";
import { Text } from "@infinitro/text";
import { onDestroy, onMount } from "svelte";
import { derived, writable } from "svelte/store";
import "../api/api";
export let raceId = undefined;
export let race = undefined;
export let maxLength = undefined;
export let inGame = true;
export let useBetFeedStyles = false;
let raceLeaderboard = [];
let isMounted = writable(false);
let startDate = undefined;
const REFRESH_INTERVAL = 1e3 * 10;
let refreshTimer = undefined;
const ready = derived([
	loggedIn,
	statePlayerId,
	isMounted
], ([$loggedIn, $statePlayerId, $isMounted]) => {
	return Boolean($isMounted && $loggedIn !== undefined && ($loggedIn === false || $statePlayerId !== undefined));
});
export let openUserInfoModal = (username) => {
	selectedProfile.set(username ?? "");
	stateOpenModal.set(ModalDialog.OtherProfile);
};
let prevById = {};
let changedWageredIds = new Set();
function getIdKey(p) {
	return p.playerId ?? p.username ?? String(p.place);
}
function getPlaceDeltaFromStart(p) {
	const cur = typeof p.place === "number" ? p.place : null;
	const start = typeof p.startPosition === "number" ? p.startPosition : null;
	if (cur == null || start == null) return 0;
	if (cur < start) return -1;
	if (cur > start) return 1;
	return 0;
}
function markWageredDeltas(next) {
	const nextById = {};
	const nextChanged = new Set(changedWageredIds);
	for (const p of next) {
		const idKey = getIdKey(p);
		const prev = prevById[idKey];
		if (prev && prev.wagered !== p.wagered) {
			nextChanged.add(idKey);
			setTimeout(() => {
				const tmp = new Set(changedWageredIds);
				tmp.delete(idKey);
				changedWageredIds = tmp;
			}, 550);
		}
		nextById[idKey] = p;
	}
	prevById = nextById;
	changedWageredIds = nextChanged;
	raceLeaderboard = raceLeaderboard;
}
let initialFetchDone = false;
let lastRaceId = undefined;
let unsubscribeReady;
$: headerTextStyle = useBetFeedStyles ? "font-size: 0.75rem !important; font-weight: 400 !important;" : "font-size: 0.75rem !important;";
$: positionTextStyle = useBetFeedStyles ? "line-height: 1.25rem; font-size: 0.875rem !important; font-weight: 400 !important;" : "line-height: 1.5rem; font-size: 1rem;";
$: valueTextStyle = useBetFeedStyles ? "line-height: 1.25rem; font-size: 0.875rem !important; font-weight: 400 !important;" : "line-height: 1.25rem; font-size: 0.875rem !important;";
const refresh = async () => {
	try {
		if (race) {
			const result = await AsyncRaceLeaderboard({
				variables: { raceId: race.raceId },
				context: { api: $loggedIn === true ? Api.player : Api.public },
				fetchPolicy: "network-only"
			});
			if (result.data?.raceLeaderboardById) {
				raceLeaderboard = result.data.raceLeaderboardById.filter((item, index) => !maxLength || index < maxLength || item.playerId === $statePlayerId);
				if (inGame) {
					markWageredDeltas(raceLeaderboard);
				}
			}
			startDate = new Date(race.startDate + "Z");
		} else {
			const result = await AsyncRaceCurrent({
				context: { api: $loggedIn === true ? Api.player : Api.public },
				fetchPolicy: "network-only"
			});
			if (result.data?.raceCurrent) {
				race = result.data.raceCurrent;
				startDate = new Date(race.startDate + "Z");
			}
			if (result.data?.raceLeaderboardCurrent) {
				raceLeaderboard = result.data.raceLeaderboardCurrent.filter((item, index) => !maxLength || index < maxLength || item.playerId === $statePlayerId);
				if (inGame) {
					markWageredDeltas(raceLeaderboard);
				}
			}
		}
		if (!refreshTimer && race && !race.gradedAt && startDate && startDate <= new Date()) {
			refreshTimer = setInterval(() => {
				refresh();
			}, REFRESH_INTERVAL);
		}
	} catch (err) {
		console.warn("Error fetching race data:", err);
		setTimeout(() => {
			refresh();
		}, 3e3);
	}
};
onMount(() => {
	isMounted.set(true);
	unsubscribeReady = ready.subscribe((isReady) => {
		if (isReady && !initialFetchDone) {
			initialFetchDone = true;
			refresh();
		}
	});
});
onDestroy(() => {
	isMounted.set(false);
	if (refreshTimer) {
		clearInterval(refreshTimer);
		refreshTimer = undefined;
	}
	if (unsubscribeReady) {
		unsubscribeReady();
		unsubscribeReady = undefined;
	}
});
$: if ($ready && raceId && raceId !== lastRaceId) {
	lastRaceId = raceId;
	raceLeaderboard = [];
	prevById = {};
	changedWageredIds = new Set();
	if (refreshTimer) {
		clearInterval(refreshTimer);
		refreshTimer = undefined;
	}
	refresh();
}
</script>

<div class="prize-pool-table-container">
	<div class="prize-pool-table">
		<div class="prize-pool-table-header" class:prize-pool-table-header-betfeed={useBetFeedStyles}>
			<div class="header position-header">
				{#if useBetFeedStyles}
					<span class="betfeed-header-label">{$t(Verbiage.rank)}</span>
				{:else}
					<Text textColor="gray" type="body" bodyWeight="bold" customStyle={headerTextStyle}>
						{$t(Verbiage.rank)}
					</Text>
				{/if}
			</div>
			<div class="header username-header">
				{#if useBetFeedStyles}
					<span class="betfeed-header-label">{$t(Verbiage.prize_pool_table_header_username)}</span>
				{:else}
					<Text textColor="gray" type="body" bodyWeight="bold" customStyle={headerTextStyle}>
						{$t(Verbiage.prize_pool_table_header_username)}
					</Text>
				{/if}
			</div>
			<div class="header wagered-header">
				{#if useBetFeedStyles}
					<span class="betfeed-header-label">{$t(Verbiage.prize_pool_table_header_wagered)}</span>
				{:else}
					<Text
						textColor="gray"
						type="body"
						bodyWeight="bold"
						customStyle={headerTextStyle}
						textAlign="end"
					>
						{$t(Verbiage.prize_pool_table_header_wagered)}
					</Text>
				{/if}
			</div>
			<div class="header prize-header">
				{#if useBetFeedStyles}
					<span class="betfeed-header-label">{$t(Verbiage.prize_pool_table_header_prize)}</span>
				{:else}
					<Text
						textColor="gray"
						type="body"
						bodyWeight="bold"
						customStyle={headerTextStyle}
						textAlign="end"
					>
						{$t(Verbiage.prize_pool_table_header_prize)}
					</Text>
				{/if}
			</div>
		</div>
		{#if !useBetFeedStyles}
			<div class="divider" />
		{/if}
		<div class="prize-pool-table-body">
			{#each raceLeaderboard as item}
				<div
					class="prize-pool-table-body-tr"
					class:prize-pool-table-body-tr-betfeed={useBetFeedStyles}
					class:prize-pool-table-body-me={item.playerId === $statePlayerId}
				>
					<div class="position">
						<Text
							type="body"
							bodyWeight={useBetFeedStyles ? 'regular' : 'bold'}
							customStyle={positionTextStyle}
						>
							<span class="pos-wrap">
								{item.place}
								{#if inGame}
									{#if getPlaceDeltaFromStart(item) === -1}
										<RemoteIcon icon={'UpChevron'} />
									{/if}
									{#if getPlaceDeltaFromStart(item) === 1}
										<RemoteIcon icon={'DownChevron'} />
									{/if}
								{/if}
							</span>
						</Text>
					</div>

					<div class="username">
						<button
							class="user-button truncated-text"
							class:user-button-betfeed={useBetFeedStyles}
							on:click={item.username ? () => openUserInfoModal(item.username ?? '') : undefined}
						>
							{#if item.username}
								{#if item.vipLevelNum != null && item.vipSublevelNum != null}
									<Icon
										rank={item.vipLevelNum}
										level={item.vipSublevelNum}
										size={$isMobile ? 16 : 20}
									/>
								{/if}
							{/if}
							<span class="username-text truncated-text">
								{$isMobile ? truncateText(item.username ?? '', 13) : (item.username ?? '')}
							</span>
						</button>
					</div>

					<div class="wagered">
						<Text
							type="body"
							bodyWeight={useBetFeedStyles ? 'regular' : 'bold'}
							customStyle={valueTextStyle}
							textAlign="end"
						>
							<span class:flash-wagered-change={changedWageredIds.has(getIdKey(item))}>
								{item.wagered
									? formatBaseCurrency(Math.floor(item.wagered / 100), false, 0, false)
									: ''}
							</span>
						</Text>
					</div>

					<div class="prize">
						<Text
							type="body"
							bodyWeight={useBetFeedStyles ? 'regular' : 'black'}
							textColor="success"
							customStyle={valueTextStyle}
							textAlign="end"
						>
							{item.prizeAmount ? formatBaseCurrency(item.prizeAmount, true, 0, false) : ''}
						</Text>
					</div>
				</div>
			{/each}
		</div>
	</div>
</div>

<style>
	.prize-pool-table-container,
	.prize-pool-table {
		width: 100%;
}

	.prize-pool-table-header {
		margin-top: 0.5rem;
		margin-bottom: 0.5rem;
		display: flex;
		justify-content: space-between;
		border-radius: 0.5rem;
		padding-top: 0.25rem;
		padding-bottom: 0.25rem;
		padding-left: 1rem;
		padding-right: 1rem;
}
	.prize-pool-table-header-betfeed {
		margin-top: 0px;
		margin-bottom: 0px;
		margin-bottom: 0.25rem;
		padding-top: 0px;
		padding-bottom: 0px;
		padding-left: 0px;
		padding-right: 0px;
}
	.prize-pool-table-header-betfeed .header {
		padding: 0.75rem;
		opacity: 0.4;
}
	@media (min-width: 640px) {

		.prize-pool-table-header-betfeed .header {
				padding-left: 1rem;
				padding-right: 1rem;
		}
}
	.betfeed-header-label {
		font-size: 0.875rem;
		font-weight: 400;
		line-height: 1.25rem;
}

	.prize-pool-table-body-tr {
		display: flex;
		align-items: center;
		justify-content: space-between;
		border-radius: 0.5rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		padding-left: 1rem;
		padding-right: 1rem;
}
	.prize-pool-table-body-tr-betfeed {
		padding-top: 0.75rem;
		padding-bottom: 0.75rem;
		padding-left: 0.75rem;
		padding-right: 0.75rem;
}
	.prize-pool-table-body-tr-betfeed:nth-child(odd) {
		background-color: color-mix(in srgb,var(--component-table-odd-row) 50%,transparent);
}
	.prize-pool-table-body-tr:not(.prize-pool-table-body-tr-betfeed):nth-child(even) {
		background-color: var(--color-background-100);
}
	.prize-pool-table-body-tr.prize-pool-table-body-me {
		background-color: var(--color-secondary-500);
}

	@keyframes bump {
		0% {
			transform: scale(1);
		}
		50% {
			transform: scale(1.4);
		}
		100% {
			transform: scale(1);
		}
	}
	@keyframes glow {
		0% {
			text-shadow: 0 0 0 currentColor;
		}
		50% {
			text-shadow: 0 0 10px currentColor;
		}
		100% {
			text-shadow: 0 0 0 currentColor;
		}
	}
	.flash-wagered-change {
		display: inline-block;
		transform-origin: right center;
		animation:
			bump 500ms ease,
			glow 500ms ease;
		will-change: transform, text-shadow;
	}

	.pos-wrap {
		display: inline-flex;
		align-items: center;
		gap: 0.25rem;
	}

	.position-header,
	.position {
		width: 12%;
}

	.username-header,
	.username {
		display: flex;
		width: 48%;
		align-items: center;
		justify-content: flex-start;
		text-align: left;
}
	.user-button {
		display: flex;
		align-items: center;
		justify-content: flex-start;
		gap: 0.25rem;
		text-align: left;
		font-size: 14px;
		font-weight: 700;
}
	.user-button-betfeed {
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 400;
}

	.wagered-header,
	.wagered,
	.prize-header,
	.prize {
		width: 25%;
		text-align: right;
}

	.divider {
		height: 1px;
		width: 100%;
		--tw-bg-opacity: 1;
		background-color: rgb(255 255 255 / var(--tw-bg-opacity, 1));
		opacity: 0.5;
}
</style>
